import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Gauge, MapTrifold, CurrencyInr, Users, ChatsCircle, Path, MapPin, UsersThree,
  Truck, QrCode, Warehouse, ShieldCheck, ChartLine, Certificate, Gear,
  Handshake, CaretLeft, CaretRight, Leaf, Calendar, CaretDown, Envelope,
  Wallet,
  GraduationCap, Buildings, Newspaper, Image, Eye, Briefcase, Printer, Scales,
  Sparkle, Phone, Bell, Clock, Heartbeat, Pill,
  ChartLineUp,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";

/* ---------------------------------------------------------------------------
 * Role-based sidebar module map.
 *
 *   roles (on group OR item) — array of allowed roles. Omit to allow ALL
 *   logged-in users.
 *
 * If a group ends up with zero visible items for a role, the group itself
 * is hidden. No disabled placeholders, no client-side hide-after-load —
 * filtered before render.
 * ------------------------------------------------------------------------- */

// Role groupings for readability
const ALL_LOGGED_IN = null; // null/omitted = all
const ADMIN_ONLY = ["superadmin", "admin"];
const MGMT = ["superadmin", "admin", "manager"];
const HR_ROLES = ["superadmin", "admin", "hr"];
const FINANCE = ["superadmin", "admin", "accountant", "manager", "office_staff", "office"];
const OPS = ["superadmin", "admin", "manager", "service_coordinator", "office_staff", "office"];
const CUSTOMER_MGMT = ["superadmin", "admin", "manager", "service_coordinator",
                        "office_staff", "office"];
const GOV_INSIGHTS = ["superadmin", "admin", "manager", "gov"];

const MODULES = [
  {
    group: "Overview",
    roles: ALL_LOGGED_IN,
    items: [{ to: "/dashboard", label: "Dashboard", icon: Gauge }],
  },
  {
    group: "1. Master Control",
    roles: MGMT,
    perm: "master",
    items: [
      { to: "/master/locations", label: "Location Hierarchy", icon: MapTrifold, perm: "master.locations" },
      { to: "/master/zones", label: "Health Zones", icon: Path, perm: "master.zones" },
      { to: "/master/schedules", label: "Collection Schedule", icon: Gauge, perm: "master.schedules" },
      { to: "/master/special-dates", label: "Special Collection Dates", icon: Calendar, perm: "master.special_dates" },
      { to: "/master/drop-locations", label: "Drop Locations", icon: MapPin, perm: "master.drop_locations" },
    ],
  },
  {
    group: "A. Staff Management",
    roles: HR_ROLES,
    perm: "workforce",
    items: [
      { to: "/admin/staff-mgmt", label: "Staff List", icon: UsersThree, perm: "workforce.staff_list" },
      { to: "/admin/staff-mgmt/new", label: "Create Staff", icon: Users, perm: "workforce.staff_create" },
      { to: "/admin/staff-mgmt/attendance", label: "Attendance & Time", icon: Gauge, perm: "workforce.attendance" },
      { to: "/admin/staff-mgmt/logs", label: "Break & Activity Logs", icon: ShieldCheck, perm: "workforce.break_logs" },
      { to: "/admin/staff-mgmt/performance", label: "Performance Tracking", icon: ChartLine, perm: "workforce.performance" },
      { to: "/admin/staff-mgmt/payroll", label: "Payroll Summary", icon: CurrencyInr, perm: "workforce.payroll" },
      { to: "/admin/staff-mgmt/roles", label: "Roles & Permissions", icon: ShieldCheck, perm: "workforce.roles" },
    ],
  },
  {
    group: "2. Service & Pricing",
    roles: MGMT,
    perm: "master",
    items: [
      { to: "/pricing/service-categories", label: "Service Categories", icon: CurrencyInr, perm: "master.service_categories" },
      { to: "/pricing/services", label: "Service Types", icon: CurrencyInr, perm: "master.services" },
      { to: "/pricing/waste-types", label: "Waste Types", icon: Leaf, perm: "master.waste_types" },
      { to: "/pricing/waste-report", label: "Waste Reports", icon: ChartLine, perm: "master.waste_report" },
      { to: "/pricing/distance", label: "Distance Pricing", icon: CurrencyInr, perm: "master.distance_pricing" },
      { to: "/pricing/charges", label: "Service Priority", icon: CurrencyInr, perm: "master.priority_charges" },
      { to: "/pricing/products", label: "Products", icon: CurrencyInr, perm: "master.products" },
      { to: "/pricing/packages", label: "Packages", icon: CurrencyInr, perm: "master.packages" },
      { to: "/pricing/calculator", label: "Price Calculator", icon: CurrencyInr, perm: "master.calculator" },
    ],
  },
  {
    group: "3. Customer Mgmt",
    roles: CUSTOMER_MGMT,
    perm: "customers",
    items: [
      { to: "/customers", label: "Customer List", icon: Users, perm: "customers.list" },
      { to: "/customers/verify", label: "Verification Queue", icon: ShieldCheck, perm: "customers.verify" },
      { to: "/customers/referrals/affiliate", label: "Referral · Affiliate", icon: Handshake, perm: "customers.referrals_affiliate" },
      { to: "/customers/referrals/partners", label: "Referral · Partners", icon: Handshake, perm: "customers.referrals_partners" },
      { to: "/customers/referrals/performance", label: "Referral · Performance", icon: ChartLine, perm: "customers.referrals_performance" },
      { to: "/customers/wallet", label: "Wallet Management", icon: Wallet, perm: "customers.list" },
      { to: "/customers/registration-certs", label: "Registration Certification", icon: Certificate, perm: "settings.registration_certs" },
    ],
  },
  {
    group: "4. CRM & Leads",
    roles: ["superadmin", "admin", "manager", "office_staff", "office"],
    perm: "crm",
    items: [
      { to: "/crm/leads", label: "Leads Pipeline", icon: ChatsCircle, perm: "crm.leads" },
      { to: "/crm/contact-submissions", label: "Contact Submissions", icon: ChatsCircle, perm: "crm.contact_submissions" },
      { to: "/crm/tickets", label: "Tickets", icon: ChatsCircle, perm: "crm.tickets" },
      { to: "/crm/tasks", label: "Tasks", icon: ChatsCircle, perm: "crm" },
      { to: "/crm/chats", label: "Chats · Rigo", icon: ChatsCircle, perm: "crm.chats" },
      { to: "/crm/handoff-queue", label: "AI Handoff Queue", icon: ChatsCircle, perm: "crm.chats", badgeApi: "/crm/handoff-queue/count", badgeTone: "danger" },
      { to: "/crm/phone", label: "Phone CRM", icon: Phone, perm: "crm.chats" },
    ],
  },
  {
    group: "5. Operations",
    roles: OPS,
    perm: "operations",
    items: [
      { to: "/operations", label: "Daily Planning & Tasks", icon: Path, perm: "operations.planning" },
      { to: "/operations/service-requests", label: "Service Requests", icon: ChatsCircle, perm: "operations.service_requests" },
      { to: "/operations/assignments", label: "Assign Field & Office Staff", icon: Users, perm: "operations.assignments",
        roles: ["superadmin", "admin", "manager", "service_coordinator", "office_staff", "office"] },
      { to: "/operations/timing", label: "Operations Timing", icon: Clock, perm: "operations.timing",
        roles: ["superadmin", "admin", "manager", "service_coordinator"] },
      { to: "/operations/skip-analytics", label: "Skip Analytics", icon: Clock, perm: "operations.skip_analytics",
        roles: ["superadmin", "admin", "manager", "service_coordinator"] },
      { to: "/operations/sr-alerts", label: "Urgent SR Push History", icon: Bell, perm: "operations.sr_alerts",
        roles: ["superadmin", "admin", "manager", "service_coordinator", "office_staff", "office"] },
      { to: "/operations/service-requests/daily", label: "Daily Collection List", icon: Calendar, perm: "operations.service_requests_daily" },
      { to: "/operations/routes/daily", label: "Route · Daily View", icon: Calendar, perm: "operations.routes_daily" },
      { to: "/operations/routes/new", label: "Route · Create Plan", icon: Path, perm: "operations.routes_new" },
      { to: "/operations/routes?status=draft", label: "Route · Optimized", icon: Path, perm: "operations.routes_optimized" },
      { to: "/operations/routes?status=assigned", label: "Route · Jobs", icon: Path, perm: "operations.routes_jobs" },
      { to: "/operations/daily-briefing", label: "Daily Briefing", icon: Sparkle, perm: "operations.daily_briefing",
        roles: ["superadmin", "admin", "manager"] },
      { to: "/live", label: "Live Ops Dashboard", icon: MapTrifold, perm: "operations.live_ops",
        roles: ["superadmin"] },
      { to: "/findgps", label: "FindGPS · Universal Tracker", icon: MapTrifold,
        roles: ["superadmin"] },
    ],
  },
  {
    group: "5b. Health Services",
    roles: ["superadmin", "admin", "manager", "service_coordinator"],
    perm: "operations",
    items: [
      { to: "/health-services", label: "Command Centre", icon: Heartbeat, perm: "operations.planning" },
      { to: "/ambulance/dispatch", label: "Ambulance Dispatch", icon: Truck, perm: "operations.planning" },
      { to: "/health-services/extras", label: "Corridor · Insurance · Fairness", icon: ShieldCheck, perm: "operations.planning" },
      { to: "/health-services/pharmacy-ops", label: "Pharmacy Ops · Rx Quotes", icon: Pill, perm: "operations.planning" },
      { to: "/health-services/wave3", label: "Wave 3 · ABDM · Payouts · AE · Reviews", icon: ShieldCheck, perm: "operations.planning" },
      { to: "/health-services/wave4", label: "Wave 4 · AirQ · Schemes · Happiness", icon: ShieldCheck, perm: "operations.planning" },
      { to: "/health-services/airq/superadmin", label: "AirQ · Founder View", icon: ShieldCheck, roles: ["superadmin","admin"] },
      { to: "/analytics/daily-care", label: "Daily Care Analytics", icon: ChartLine, perm: "operations.planning" },
    ],
  },
  {
    group: "6. Field Workforce",
    roles: ["superadmin", "admin", "manager", "hr", "service_coordinator"],
    perm: "workforce",
    items: [
      { to: "/staff", label: "Field Staff", icon: UsersThree, perm: "workforce.field_staff" },
      { to: "/workforce/leaderboard", label: "Leaderboard", icon: ChartLine, perm: "workforce.leaderboard" },
      { to: "/workforce/shifts", label: "Shift Scheduler", icon: Gauge, perm: "workforce.shifts",
        roles: ["superadmin", "admin", "manager", "hr"] },
      { to: "/workforce/payroll", label: "Payroll", icon: CurrencyInr, perm: "workforce.payroll",
        roles: ["superadmin", "admin", "manager", "hr"] },
      { to: "/fleet", label: "Fleet & Vehicles", icon: Truck, perm: "workforce.fleet" },
      { to: "/admin/gig-partners", label: "Gig Partners · Marketplace", icon: UsersThree,
        roles: ["superadmin", "admin", "manager", "onboarding_manager"] },
      { to: "/admin/onboarding-managers", label: "Onboarding Managers", icon: UsersThree,
        roles: ["superadmin", "admin", "manager"] },
    ],
  },
  {
    group: "7. Chain of Custody",
    roles: ["superadmin", "admin", "manager", "service_coordinator"],
    perm: "operations",
    items: [
      { to: "/qr", label: "QR & Inventory", icon: QrCode, perm: "operations.qr_inventory" },
      { to: "/disposal", label: "Disposal Center", icon: Warehouse, perm: "operations.disposal" },
    ],
  },
  {
    group: "8. Risk & Finance",
    roles: FINANCE,
    perm: "finance",
    items: [
      { to: "/fraud", label: "Fraud & Control", icon: ShieldCheck, perm: "finance.fraud",
        roles: ["superadmin", "admin", "manager", "office_staff", "office"] },
      { to: "/finance/invoices", label: "Invoices", icon: CurrencyInr, perm: "finance.invoices" },
      { to: "/finance/invoices-pending", label: "Invoice Pending", icon: CurrencyInr, perm: "finance.invoices_pending" },
      { to: "/finance/invoice-analytics", label: "Invoice Analytics", icon: ChartLineUp, perm: "finance",
        roles: ["superadmin", "admin", "manager", "finance", "accountant"] },
      { to: "/finance/transactions", label: "Transactions", icon: CurrencyInr, perm: "finance.transactions" },
      { to: "/finance/payments", label: "Razorpay Payments", icon: CurrencyInr, perm: "finance.razorpay" },
      { to: "/finance/wallet-txns", label: "Wallet Transactions", icon: Wallet, perm: "finance.razorpay" },
      { to: "/admin/manual-payments", label: "Manual UPI Verifications", icon: ShieldCheck, perm: "finance.manual_upi",
        roles: ["superadmin", "admin", "manager"] },
      { to: "/finance/emails", label: "Email Log", icon: Envelope, perm: "finance.emails" },
      { to: "/finance/expenses", label: "Expenses", icon: Wallet, perm: "finance.expenses" },
      { to: "/finance/revenue", label: "Revenue", icon: ChartLine, perm: "finance.revenue" },
      { to: "/finance/rollup", label: "Rollup & CRM", icon: ChartLine, perm: "finance.rollup" },
      { to: "/finance/partner-payouts", label: "Partner Payouts", icon: CurrencyInr,
        roles: ["superadmin", "admin", "manager"] },
      { to: "/finance/settings", label: "Finance Settings", icon: Gear, perm: "finance.settings",
        roles: ADMIN_ONLY },
    ],
  },
  {
    group: "9. Insights & Impact",
    roles: GOV_INSIGHTS,
    perm: "reports",
    items: [
      { to: "/analytics", label: "Analytics & Reports", icon: ChartLine, perm: "reports.analytics" },
      { to: "/certificates", label: "Certificates", icon: Certificate, perm: "certificates.list" },
      { to: "/admin/institutional-certs", label: "Institutional Certs", icon: Certificate, perm: "certificates.institutional" },
    ],
  },
  {
    group: "10. Training Academy",
    roles: MGMT,
    perm: "certificates",
    items: [
      { to: "/training-admin", label: "LMS Configuration", icon: GraduationCap, perm: "certificates.lms_config" },
      { to: "/admin/training-partners", label: "Institutional Partners", icon: Buildings, perm: "certificates.training_partners" },
      { to: "/training", label: "Public Academy", icon: Certificate, perm: "certificates.training_public" },
    ],
  },
  {
    group: "11. Content Management",
    roles: ADMIN_ONLY,
    perm: "settings",
    items: [
      { to: "/admin/content", label: "Publications CMS", icon: Newspaper, perm: "settings.publications" },
      { to: "/admin/content/services-page", label: "/services page settings",
        icon: Image, roles: ["superadmin", "admin"] },
      { to: "/admin/content/page-images", label: "Page Images",
        icon: Image, roles: ["superadmin", "admin"] },
      { to: "/admin/customer-app-cms", label: "Customer App CMS", icon: Phone, perm: "settings.publications" },
      { to: "/admin/ad-banners", label: "Ad Banner Manager", icon: Image, perm: "settings.publications" },
      { to: "/admin/alarm-sounds", label: "Alarm Sounds", icon: Sparkle, perm: "settings.publications" },
      { to: "/admin/about", label: "About Us · Editor", icon: Newspaper, perm: "settings.about_editor" },
      { to: "/admin/nav-menus", label: "Menu Navigation", icon: Eye, perm: "settings.nav_menus" },
      { to: "/admin/careers", label: "Job Postings", icon: Briefcase, perm: "settings.careers" },
      { to: "/admin/agencies", label: "Agency Directory", icon: Buildings, perm: "settings.agencies" },
      { to: "/admin/registration-certs", label: "Registration Certs", icon: Certificate, perm: "settings.registration_certs" },
      { to: "/admin/media", label: "Media Library", icon: Image, perm: "settings.media" },
      { to: "/admin/site-pages", label: "Public Pages Visibility", icon: Eye, perm: "settings.site_pages" },
    ],
  },
  {
    group: "12. Administration",
    roles: ADMIN_ONLY,
    perm: "settings",
    items: [
      { to: "/admin/service-partners", label: "Service Partners", icon: Handshake, perm: "settings.service_partners" },
      { to: "/admin/hospitals", label: "Hospital Directory", icon: Buildings, perm: "settings.service_partners" },
      { to: "/admin/gov-users", label: "Government Users", icon: Buildings, perm: "settings.gov_users" },
      { to: "/franchise", label: "Franchise Management", icon: Handshake, perm: "settings.franchise" },
      { to: "/settings", label: "System Settings", icon: Gear, perm: "settings.general" },
      { to: "/settings/printer", label: "Bluetooth Printer", icon: Printer, perm: "settings.printer", roles: null },
      { to: "/settings/scale", label: "Weight Scale", icon: Scales, perm: "settings.scale", roles: null },
      { to: "/settings/chat-bot", label: "Chat Bot · Rigo", icon: ChatsCircle, perm: "settings.chat_bot", roles: null },
      { to: "/settings/reply-templates", label: "Reply Templates", icon: ChatsCircle, perm: "settings.reply_templates", roles: null },
      { to: "/admin/otp-settings", label: "OTP Settings", icon: ShieldCheck, perm: "settings.otp" },
      { to: "/admin/data-sync", label: "Data Sync (Prod ⇄ Builder)", icon: ShieldCheck, perm: "settings.data_sync" },
    ],
  },
  {
    group: "System",
    roles: ADMIN_ONLY,
    perm: "settings",
    items: [
      { to: "/audit-logs", label: "Audit Logs", icon: ShieldCheck, perm: "settings.audit_logs" },
    ],
  },
];

/** Returns true when role passes the optional `roles` array (null = open). */
function _allows(roles, role) {
  return !roles || roles.includes(role);
}

/**
 * Returns true when the user's effective permission list satisfies the
 * given permission `key`.  Supports both coarse module scopes ("finance")
 * and fully-qualified submodule keys ("finance.emails.view"). Falls back
 * to `true` when either `key` is missing (open module) or when `perms`
 * isn't loaded yet (backward compat with older /auth/me responses).
 */
function _hasPerm(perms, key) {
  if (!key) return true;
  if (!Array.isArray(perms)) return true;
  if (perms.length === 0) return false;
  if (perms.includes("*")) return true;
  if (perms.includes(key)) return true;
  // Walk every parent scope so a coarse wildcard grants finer keys:
  //   perms = ["finance.*"], key = "finance.emails" ⇒ true
  //   perms = ["finance.emails.*"], key = "finance.emails.view" ⇒ true
  const parts = key.split(".");
  for (let i = 1; i <= parts.length; i++) {
    const scope = parts.slice(0, i).join(".");
    if (perms.includes(scope) || perms.includes(`${scope}.*`)) return true;
  }
  // Any child of key present ⇒ partial access counts as visible.
  const prefix = `${key}.`;
  return perms.some(p => p.startsWith(prefix));
}

/** Filter modules + items for the current role AND effective permissions.
 *  A group is hidden when either the role doesn't pass OR the tenant
 *  admin has revoked every capability for that module scope.  Individual
 *  items may additionally declare their own `perm` (submodule key like
 *  `finance.emails`) — when set, the item is hidden unless the user has
 *  that specific permission. */
function visibleFor(role, perms) {
  return MODULES
    .filter(g => _allows(g.roles, role))
    .filter(g => _hasPerm(perms, g.perm))
    .map(g => ({
      ...g,
      items: g.items.filter(it =>
        _allows(it.roles, role) && _hasPerm(perms, it.perm),
      ),
    }))
    .filter(g => g.items.length > 0);
}

const STORAGE_KEY = "rescigo.sidebar.openGroup";

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined" && window.innerWidth < 768);
  // Iter 142 — keep the sidebar collapsed on narrow viewports so mobile
  // gets the full width for content. On resize past the mobile
  // breakpoint the user can toggle it back open manually.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth < 768) setCollapsed(true);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const location = useLocation();
  const { user } = useAuth();
  const role = user?.role;
  const perms = user?.permissions;

  const groups = useMemo(() => visibleFor(role, perms), [role, perms]);

  // Determine which group should be open by default:
  //  1. If active route matches a group → that group
  //  2. Else stored from last session
  //  3. Else first visible group
  const findGroupOfPath = (pathname) =>
    groups.find(g =>
      g.items.some(it => {
        const itPath = it.to.split("?")[0];
        return pathname === itPath || pathname.startsWith(itPath + "/");
      }))?.group;

  const [openGroup, setOpenGroup] = useState(() => {
    const stored = typeof window !== "undefined"
      ? localStorage.getItem(STORAGE_KEY) : null;
    return stored || null;
  });

  // Sync openGroup with active route on mount + path change
  useEffect(() => {
    const match = findGroupOfPath(location.pathname);
    if (match && match !== openGroup) {
      setOpenGroup(match);
    } else if (!openGroup && groups.length > 0) {
      // First-time login: open first allowed module
      setOpenGroup(groups[0].group);
    }
    // eslint-disable-next-line
  }, [location.pathname, groups.length]);

  // Persist
  useEffect(() => {
    if (openGroup) localStorage.setItem(STORAGE_KEY, openGroup);
  }, [openGroup]);

  const isCoordinator = role === "service_coordinator";

  if (!user) return null;

  return (
    <aside data-testid="sidebar"
      className={`${collapsed ? "w-16" : "w-64"} shrink-0 border-r border-[#E5E2D9] bg-white sticky top-0 h-screen overflow-y-auto transition-[width] duration-200`}>
      <div className="flex items-center gap-2 p-4 border-b border-[#E5E2D9] h-16">
        <div className={`w-9 h-9 rounded-md ${isCoordinator ? "bg-[#4A7C59]" : "bg-[#2D5A27]"} flex items-center justify-center shrink-0`}>
          <Leaf size={20} color="#FFFFFF" weight="fill"/>
        </div>
        {!collapsed && (
          <div className="flex-1">
            <div className="font-heading font-semibold text-[15px] leading-tight text-[#1A1A1A]">Rescigo</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
              {isCoordinator ? "Coordinator Panel" : "Admin Control"}
            </div>
          </div>
        )}
        <button data-testid="sidebar-toggle"
                onClick={() => setCollapsed(c => !c)}
                className="p-1.5 rounded hover:bg-[#EFEDE5] text-[#5C5A55]"
                title={collapsed ? "Expand" : "Collapse"}>
          {collapsed ? <CaretRight size={14}/> : <CaretLeft size={14}/>}
        </button>
      </div>

      <nav className="pb-6" data-testid="sidebar-nav">
        {groups.map((g) => {
          const isOpen = openGroup === g.group || collapsed;
          const containsActive = g.items.some(it => {
            const itPath = it.to.split("?")[0];
            return location.pathname === itPath ||
                    location.pathname.startsWith(itPath + "/");
          });
          // Single-item groups: render flat (no caret).
          const singleItem = g.items.length === 1;
          const slug = g.group.toLowerCase().replace(/[^a-z0-9]+/g, "-");
          if (collapsed) {
            return (
              <div key={g.group}>
                <div className="h-3"/>
                {g.items.map((it) => (
                  <SidebarLink key={it.to} item={it} collapsed pathname={location.pathname}/>
                ))}
              </div>
            );
          }
          return (
            <div key={g.group} data-testid={`sb-group-${slug}`}>
              {singleItem ? (
                <>
                  <div className="sidebar-group-label">{g.group}</div>
                  <SidebarLink item={g.items[0]} pathname={location.pathname}/>
                </>
              ) : (
                <>
                  <button onClick={() => setOpenGroup(isOpen ? null : g.group)}
                          data-testid={`sb-group-toggle-${slug}`}
                          className={`w-full flex items-center justify-between sidebar-group-label group/btn cursor-pointer hover:text-[#2D5A27] ${containsActive ? "text-[#2D5A27]" : ""}`}>
                    <span>{g.group}</span>
                    <CaretDown size={10}
                      className={`transition-transform duration-150 ${isOpen ? "rotate-0" : "-rotate-90"}`}/>
                  </button>
                  <div className={`overflow-hidden transition-[max-height] duration-200 ${isOpen ? "max-h-[1500px]" : "max-h-0"}`}>
                    {g.items.map(it => <SidebarLink key={it.to} item={it} pathname={location.pathname}/>)}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

function SidebarLink({ item, collapsed, pathname }) {
  const itPath = item.to.split("?")[0];
  const testIdSlug = item.to.replace(/[^a-z0-9]/gi, "-").replace(/^-+|-+$/g, "");
  const active = pathname === itPath || pathname.startsWith(itPath + "/");
  const Icon = item.icon;
  const [badge, setBadge] = React.useState(null);
  React.useEffect(() => {
    if (!item.badgeApi) return;
    let alive = true;
    const load = () => api.get(item.badgeApi)
      .then(r => { if (alive) setBadge(r.data?.count || 0); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [item.badgeApi]);
  return (
    <NavLink to={item.to} end={false}
      data-testid={`nav-${testIdSlug}`}
      className={`sidebar-item ${active ? "active" : ""} ${collapsed ? "justify-center px-0" : ""}`}
      title={collapsed ? item.label : undefined}>
      <Icon size={18} weight={active ? "fill" : "regular"}/>
      {!collapsed && <span className="truncate flex-1">{item.label}</span>}
      {!collapsed && badge > 0 && (
        <span className={`ml-auto px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
          item.badgeTone === "danger"
            ? "bg-[#C25D38] text-white"
            : "bg-[#2D5A27] text-white"
        }`}
              data-testid={`nav-badge-${testIdSlug}`}>
          {badge}
        </span>
      )}
    </NavLink>
  );
}
